import React from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { ChakraProvider, extendTheme, Flex, Spinner } from "@chakra-ui/react";
import { EmployeeAuthProvider, useEmployeeAuth } from "./auth/EmployeeAuthContext";
import Shell from "./components/Shell";
import baseTheme from "../theme/theme";
import { EMP_BASE_PATH } from "./config";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import CheckIn from "./pages/CheckIn";
import AttendanceCalendar from "./pages/AttendanceCalendar";
import AttendanceSummary from "./pages/AttendanceSummary";
import LeaveList from "./pages/leave/LeaveList";
import LeaveCreate from "./pages/leave/LeaveCreate";
import LeaveDetail from "./pages/leave/LeaveDetail";
import OutsideList from "./pages/outside/OutsideList";
import OutsideCreate from "./pages/outside/OutsideCreate";
import OutsideDetail from "./pages/outside/OutsideDetail";
import MakeupList from "./pages/makeup/MakeupList";
import MakeupCreate from "./pages/makeup/MakeupCreate";
import MakeupDetail from "./pages/makeup/MakeupDetail";
import AccountLanding from "./pages/AccountLanding";
import AccountSettings from "./pages/AccountSettings";
import SignatureList from "./pages/signature/SignatureList";
import SignatureCreate from "./pages/signature/SignatureCreate";
import Maintenance from "./pages/Maintenance";
import RegisterEmployee from "./pages/RegisterEmployee";
import ProjectList from "./pages/finance/ProjectList";
import ProjectDetail from "./pages/finance/ProjectDetail";
import SalesOpeningEventList from "./pages/finance/SalesOpeningEventList";
import SalesOpeningEventDetail from "./pages/finance/SalesOpeningEventDetail";
import CustomerList from "./pages/finance/CustomerList";
import CustomerDetail from "./pages/finance/CustomerDetail";
import CustomerNew from "./pages/finance/CustomerNew";
import CustomerForm from "./pages/finance/CustomerForm";
import BookingList from "./pages/finance/BookingList";
import BookingDetail from "./pages/finance/BookingDetail";
import TransferRequest from "./pages/finance/TransferRequest";
import LockingList from "./pages/finance/LockingList";
import LockingDetail from "./pages/finance/LockingDetail";
import LockingRelativeForm from "./pages/finance/LockingRelativeForm";
import TtkqList from "./pages/finance/TtkqList";
import TtkqDetail from "./pages/finance/TtkqDetail";
import SalesContractList from "./pages/finance/SalesContractList";
import SalesContractDetail from "./pages/finance/SalesContractDetail";
import TransferTransactionList from "./pages/finance/TransferTransactionList";
import TransferTransactionDetail from "./pages/finance/TransferTransactionDetail";
import CollaboratorIndex from "./pages/finance/CollaboratorIndex";
import RelativeList from "./pages/finance/RelativeList";
import CollaboratorRequestList from "./pages/finance/CollaboratorRequestList";
import CollaboratorRequestDetail from "./pages/finance/CollaboratorRequestDetail";
import CollaboratorRequestForm from "./pages/finance/CollaboratorRequestForm";
import IncomeIndex from "./pages/finance/IncomeIndex";
import IncomeList from "./pages/finance/IncomeList";
import IncomeUnpaidList from "./pages/finance/IncomeUnpaidList";
import IncomeDetail from "./pages/finance/IncomeDetail";
import BdcIndex from "./pages/finance/BdcIndex";
import BdcHeld from "./pages/finance/BdcHeld";
import BdcDisbursement from "./pages/finance/BdcDisbursement";
import ApartmentSelect from "./pages/finance/ApartmentSelect";
import TransactionCreate from "./pages/finance/TransactionCreate";
import TopicList from "./pages/training/TopicList";
import LectureList from "./pages/training/LectureList";
import LessonList from "./pages/training/LessonList";
import LessonDetail from "./pages/training/LessonDetail";
import DocumentViewer from "./pages/training/DocumentViewer";
import Quiz from "./pages/training/Quiz";
import MyEvaluationList from "./pages/evaluation/MyEvaluationList";
import MyKpiDetail from "./pages/evaluation/MyKpiDetail";
import LeaveApprovalList from "./pages/management/LeaveApprovalList";
import LeaveApprovalDetail from "./pages/management/LeaveApprovalDetail";
import OutsideApprovalList from "./pages/management/OutsideApprovalList";
import OutsideApprovalDetail from "./pages/management/OutsideApprovalDetail";
import CcmgIndex from "./pages/management/ccmg/CcmgIndex";
import CcmgEmployeeList from "./pages/management/ccmg/CcmgEmployeeList";
import CcmgHeldDetail from "./pages/management/ccmg/CcmgHeldDetail";
import CcmgDisbursementDetail from "./pages/management/ccmg/CcmgDisbursementDetail";
import ReportIndex from "./pages/management/report/ReportIndex";
import EmployeeKpiList from "./pages/management/report/EmployeeKpiList";
import EmployeeKpiDetail from "./pages/management/report/EmployeeKpiDetail";
import AddEvaluation from "./pages/management/report/AddEvaluation";
import IncomeManagerIndex from "./pages/management/income/IncomeManagerIndex";
import IncomeCompareList from "./pages/management/income/IncomeCompareList";
import IncomeLockingList from "./pages/management/income/IncomeLockingList";
import IncomeLockingEmployeeDetail from "./pages/management/income/IncomeLockingEmployeeDetail";
import IncomeUnpaidEmployeeList from "./pages/management/income/IncomeUnpaidEmployeeList";
import IncomeUnpaidLockingList from "./pages/management/income/IncomeUnpaidLockingList";
import BlockKpiList from "./pages/management/report/BlockKpiList";
import BlockKpiDetail from "./pages/management/report/BlockKpiDetail";
import StaffIndex from "./pages/management/staff/StaffIndex";
import StaffEmployeeList from "./pages/management/staff/StaffEmployeeList";
import StaffEmployeeMenu from "./pages/management/staff/StaffEmployeeMenu";
import StaffEmployeeDetail from "./pages/management/staff/StaffEmployeeDetail";
import AttendanceEmployeeList from "./pages/management/attendance/AttendanceEmployeeList";
import AttendanceDetail from "./pages/management/attendance/AttendanceDetail";
import SellIndex from "./pages/management/sell/SellIndex";
import SellTransactionList from "./pages/management/sell/SellTransactionList";
import SellTransactionDetail from "./pages/management/sell/SellTransactionDetail";
import CheckTransaction from "./pages/management/sell/CheckTransaction";

// Theme riêng cho cổng nhân viên: bỏ nền xám mặc định của Button trong theme app
// (theme app đặt Button.baseStyle.backgroundColor = var(--color-background) = xám).
// Nút primary vẫn hiển thị gradient nhờ bgImage; outline/ghost sẽ là nền trắng/trong.
const empTheme = extendTheme(
  { components: { Button: { baseStyle: { backgroundColor: "transparent" } } } },
  baseTheme
);

const FullScreenLoading = () => (
  <Flex minH="100vh" align="center" justify="center" bg="var(--color-backgroundmain)">
    <Spinner thickness="4px" speed="0.65s" color="var(--color-secondary)" size="xl" />
  </Flex>
);

const ProtectedLayout = () => (
  <Shell>
    <Outlet />
  </Shell>
);

const EmployeeRoutes = () => {
  const { isAuthenticated, booting } = useEmployeeAuth();

  if (booting) return <FullScreenLoading />;

  return (
    <Routes>
      <Route
        path={`${EMP_BASE_PATH}/login`}
        element={isAuthenticated ? <Navigate to={EMP_BASE_PATH} replace /> : <Login />}
      />

      <Route
        element={
          isAuthenticated ? (
            <ProtectedLayout />
          ) : (
            <Navigate to={`${EMP_BASE_PATH}/login`} replace />
          )
        }
      >
        <Route path={EMP_BASE_PATH} element={<Dashboard />} />
        <Route path={`${EMP_BASE_PATH}/cham-cong`} element={<CheckIn />} />
        <Route path={`${EMP_BASE_PATH}/chuyen-can`} element={<AttendanceCalendar />} />
        <Route path={`${EMP_BASE_PATH}/chuyen-can/chi-tiet`} element={<AttendanceSummary />} />

        <Route path={`${EMP_BASE_PATH}/nghi-phep`} element={<LeaveList />} />
        <Route path={`${EMP_BASE_PATH}/nghi-phep/tao`} element={<LeaveCreate />} />
        <Route path={`${EMP_BASE_PATH}/nghi-phep/:id`} element={<LeaveDetail />} />

        <Route path={`${EMP_BASE_PATH}/cham-cong-ngoai`} element={<OutsideList />} />
        <Route path={`${EMP_BASE_PATH}/cham-cong-ngoai/tao`} element={<OutsideCreate />} />
        <Route path={`${EMP_BASE_PATH}/cham-cong-ngoai/:id`} element={<OutsideDetail />} />

        <Route path={`${EMP_BASE_PATH}/lam-bu`} element={<MakeupList />} />
        <Route path={`${EMP_BASE_PATH}/lam-bu/tao`} element={<MakeupCreate />} />
        <Route path={`${EMP_BASE_PATH}/lam-bu/:id`} element={<MakeupDetail />} />

        <Route path={`${EMP_BASE_PATH}/tai-khoan`} element={<AccountLanding />} />
        <Route path={`${EMP_BASE_PATH}/tai-khoan/thong-tin`} element={<AccountSettings />} />

        <Route path={`${EMP_BASE_PATH}/chu-ky`} element={<SignatureList />} />
        <Route path={`${EMP_BASE_PATH}/chu-ky/tao`} element={<SignatureCreate />} />

        <Route path={`${EMP_BASE_PATH}/bao-tri`} element={<Maintenance />} />
        <Route path={`${EMP_BASE_PATH}/gioi-thieu-viec-lam`} element={<RegisterEmployee />} />

        <Route path={`${EMP_BASE_PATH}/tai-chinh/du-an`} element={<ProjectList />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/du-an/:id`} element={<ProjectDetail />} />

        <Route path={`${EMP_BASE_PATH}/tai-chinh/dot-mo-ban`} element={<SalesOpeningEventList />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/dot-mo-ban/:id`} element={<SalesOpeningEventDetail />} />

        <Route path={`${EMP_BASE_PATH}/tai-chinh/khach-hang`} element={<CustomerList />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/khach-hang/tao`} element={<CustomerNew />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/khach-hang/tao/:kind`} element={<CustomerForm />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/khach-hang/:id`} element={<CustomerDetail />} />

        <Route path={`${EMP_BASE_PATH}/tai-chinh/chon-can-ho`} element={<ApartmentSelect />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/booking`} element={<BookingList />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/booking/tao`} element={<TransactionCreate mode="booking" />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/booking/chuyen/:id`} element={<TransferRequest kind="booking" />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/booking/:id`} element={<BookingDetail />} />

        <Route path={`${EMP_BASE_PATH}/tai-chinh/locking`} element={<LockingList />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/locking/tao`} element={<TransactionCreate mode="locking" />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/locking/chuyen/:id`} element={<TransferRequest kind="locking" />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/locking/:id/ctv`} element={<LockingRelativeForm />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/locking/:id`} element={<LockingDetail />} />

        <Route path={`${EMP_BASE_PATH}/tai-chinh/ttkq`} element={<TtkqList />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/ttkq/chuyen/:id`} element={<TransferRequest kind="ttkq" />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/ttkq/:id`} element={<TtkqDetail />} />

        <Route path={`${EMP_BASE_PATH}/tai-chinh/hop-dong-mua-ban`} element={<SalesContractList />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/hop-dong-mua-ban/chuyen/:id`} element={<TransferRequest kind="salesContract" />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/hop-dong-mua-ban/:id`} element={<SalesContractDetail />} />

        <Route path={`${EMP_BASE_PATH}/tai-chinh/chuyen-giao-dich`} element={<TransferTransactionList />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/chuyen-giao-dich/:id`} element={<TransferTransactionDetail />} />

        <Route path={`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien`} element={<CollaboratorIndex />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien/danh-sach`} element={<RelativeList />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien/phieu`} element={<CollaboratorRequestList />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien/phieu/tao`} element={<CollaboratorRequestForm />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien/phieu/:id`} element={<CollaboratorRequestDetail />} />

        <Route path={`${EMP_BASE_PATH}/tai-chinh/thu-nhap`} element={<IncomeIndex />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/thu-nhap/cho-chi-tra`} element={<IncomeUnpaidList />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/thu-nhap/:kind`} element={<IncomeList />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/thu-nhap/:kind/:id`} element={<IncomeDetail />} />

        <Route path={`${EMP_BASE_PATH}/tai-chinh/chung-chi-moi-gioi`} element={<BdcIndex />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/chung-chi-moi-gioi/tam-giu`} element={<BdcHeld />} />
        <Route path={`${EMP_BASE_PATH}/tai-chinh/chung-chi-moi-gioi/giai-ngan`} element={<BdcDisbursement />} />

        <Route path={`${EMP_BASE_PATH}/dao-tao`} element={<TopicList />} />
        <Route path={`${EMP_BASE_PATH}/dao-tao/chu-de/:topicId`} element={<LectureList />} />
        <Route path={`${EMP_BASE_PATH}/dao-tao/bai-giang/:lectureId`} element={<LessonList />} />
        <Route path={`${EMP_BASE_PATH}/dao-tao/bai-hoc/:lessonId/tai-lieu/:attachmentId`} element={<DocumentViewer />} />
        <Route path={`${EMP_BASE_PATH}/dao-tao/bai-hoc/:lessonId/trac-nghiem`} element={<Quiz />} />
        <Route path={`${EMP_BASE_PATH}/dao-tao/bai-hoc/:lessonId`} element={<LessonDetail />} />

        <Route path={`${EMP_BASE_PATH}/danh-gia-ca-nhan`} element={<MyEvaluationList />} />
        <Route path={`${EMP_BASE_PATH}/danh-gia-ca-nhan/chi-tiet`} element={<MyKpiDetail />} />

        <Route path={`${EMP_BASE_PATH}/quan-ly/phe-duyet-nghi-phep`} element={<LeaveApprovalList />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/phe-duyet-nghi-phep/:id`} element={<LeaveApprovalDetail />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/phe-duyet-cham-cong-ngoai`} element={<OutsideApprovalList />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/phe-duyet-cham-cong-ngoai/:id`} element={<OutsideApprovalDetail />} />

        <Route path={`${EMP_BASE_PATH}/quan-ly/ccmg`} element={<CcmgIndex />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/ccmg/tam-giu`} element={<CcmgEmployeeList mode="held" />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/ccmg/tam-giu/:id`} element={<CcmgHeldDetail />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/ccmg/thanh-toan`} element={<CcmgEmployeeList mode="disbursement" />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/ccmg/thanh-toan/:id`} element={<CcmgDisbursementDetail />} />

        <Route path={`${EMP_BASE_PATH}/quan-ly/bao-cao`} element={<ReportIndex />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/bao-cao/kpi-khoi`} element={<BlockKpiList />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/bao-cao/kpi-khoi/:id`} element={<BlockKpiDetail />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/bao-cao/kpi-nhan-vien`} element={<EmployeeKpiList />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/bao-cao/kpi-nhan-vien/:code/them-danh-gia`} element={<AddEvaluation />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/bao-cao/kpi-nhan-vien/:code`} element={<EmployeeKpiDetail />} />

        <Route path={`${EMP_BASE_PATH}/quan-ly/thu-nhap`} element={<IncomeManagerIndex />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/thu-nhap/cho-chi-tra`} element={<IncomeUnpaidEmployeeList />} />
        <Route
          path={`${EMP_BASE_PATH}/quan-ly/thu-nhap/cho-chi-tra/:employeeId`}
          element={<IncomeUnpaidLockingList />}
        />
        <Route path={`${EMP_BASE_PATH}/quan-ly/thu-nhap/:kind`} element={<IncomeCompareList />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/thu-nhap/:kind/:id/can-ho`} element={<IncomeLockingEmployeeDetail />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/thu-nhap/:kind/:id`} element={<IncomeLockingList />} />

        <Route path={`${EMP_BASE_PATH}/quan-ly/nhan-vien`} element={<StaffIndex />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/nhan-vien/danh-sach`} element={<StaffEmployeeList />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/nhan-vien/:id/thong-tin`} element={<StaffEmployeeDetail />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/nhan-vien/:id`} element={<StaffEmployeeMenu />} />

        <Route path={`${EMP_BASE_PATH}/quan-ly/chuyen-can`} element={<AttendanceEmployeeList />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/chuyen-can/:id`} element={<AttendanceDetail />} />

        <Route path={`${EMP_BASE_PATH}/quan-ly/ban-hang`} element={<SellIndex />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/ban-hang/kiem-tra-giao-dich`} element={<CheckTransaction />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/ban-hang/:type`} element={<SellTransactionList />} />
        <Route path={`${EMP_BASE_PATH}/quan-ly/ban-hang/:type/:id`} element={<SellTransactionDetail />} />
      </Route>

      <Route path={`${EMP_BASE_PATH}/*`} element={<Navigate to={EMP_BASE_PATH} replace />} />
    </Routes>
  );
};

const EmployeeApp = () => (
  <ChakraProvider theme={empTheme} resetCSS={false}>
    <EmployeeAuthProvider>
      <EmployeeRoutes />
    </EmployeeAuthProvider>
  </ChakraProvider>
);

export default EmployeeApp;
